import React, { useState } from 'react';
import { Badge, Text } from '../../design-system';
import SpecCodeCompare from './SpecCodeCompare';

const T = {
  hand: '"Caveat", "Nunito", cursive',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  ink: '#2E2109',
  ink2: '#4A3510',
  inkMuted: '#6E5A38',
  inkFaint: '#A39068',
  orange: '#EA7C1F',
  orangeDark: '#C25F0E',
  capRed: '#C14A33',
  moss: '#6B7A3E',
};

const SEVERITY_COLOR = {
  high: T.capRed,
  medium: T.orange,
  low: T.moss,
};

const DIRECTION_LABEL = { code_ahead: '→ CODE', spec_ahead: '← SPEC' };
const DIRECTION_SUBLINE = {
  code_ahead: 'In code, not in spec',
  spec_ahead: 'In spec, not in code',
};

const SHA_STYLE = {
  fontFamily: T.mono,
  fontSize: 12,
  color: T.orangeDark,
  background: 'transparent',
  letterSpacing: '0.02em',
};

function formatRelativeTime(iso) {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return 'just now';
  const m = Math.floor(diffMs / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function SeverityLabel({ severity }) {
  const sev = severity || 'low';
  const color = SEVERITY_COLOR[sev] || T.inkMuted;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: color,
        display: 'inline-block', flexShrink: 0,
      }} />
      <span style={{
        fontFamily: T.mono, fontSize: 11, fontWeight: 800,
        letterSpacing: '0.6px', textTransform: 'uppercase', color,
      }}>
        {sev}
      </span>
    </span>
  );
}

function DirectionPill({ direction }) {
  return (
    <span style={{
      fontFamily: T.mono, fontSize: 11, fontWeight: 800,
      letterSpacing: '0.5px',
      color: T.orangeDark,
      backgroundColor: 'rgba(234,124,31,0.20)',
      border: '1px solid rgba(234,124,31,0.42)',
      padding: '3px 9px', borderRadius: 999, lineHeight: 1,
      whiteSpace: 'nowrap',
    }}>
      {DIRECTION_LABEL[direction] || direction}
    </span>
  );
}

export default function DriftFindingRow({
  finding,
  showRepoBadge,
  onUpdateSpec,
  updateSpecRunning,
  onDismiss,
  dismissRunning,
  scannedAt,
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ borderTop: '1px solid var(--color-border)' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          cursor: 'pointer',
          gap: 14,
        }}
      >
        <SeverityLabel severity={finding.severity} />
        <DirectionPill direction={finding.direction} />
        <span style={{
          flex: 1,
          fontFamily: T.sans, fontSize: 14, fontWeight: 600,
          color: T.ink,
          minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {finding.title}
        </span>
        {showRepoBadge && <Badge variant="neutral">{finding.repo}</Badge>}
        {finding.commit_sha && <span style={SHA_STYLE}>{finding.commit_sha.slice(0, 7)}</span>}
        <span style={{ fontFamily: T.sans, fontSize: 12, color: T.inkMuted }}>
          {formatRelativeTime(scannedAt)}
        </span>
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 16px 16px' }}>
          <div style={{ fontFamily: T.sans, fontSize: 12, color: T.inkMuted, marginBottom: 12 }}>
            <span style={{ color: T.orangeDark, fontWeight: 600 }}>
              {finding.direction === 'code_ahead' ? '→ Code ahead' : '← Spec ahead'}
            </span>
            <span> · {DIRECTION_SUBLINE[finding.direction]}</span>
            <span> · <span style={{ fontFamily: T.mono, fontSize: 11 }}>{finding.file_path}</span></span>
            {finding.author && <span> · by {finding.author}</span>}
            {finding.related_story_id && (
              <span> · Story <span style={{ color: T.orangeDark, fontWeight: 600 }}>{finding.related_story_id}</span></span>
            )}
          </div>

          <SpecCodeCompare specSays={finding.spec_says} codeSays={finding.code_says} />

          <div style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 8,
            border: '1px solid rgba(191,171,121,0.55)',
            background: 'rgba(214,196,154,0.32)',
          }}>
            <div style={{
              fontFamily: T.mono, fontSize: 10, fontWeight: 800,
              letterSpacing: '0.6px', textTransform: 'uppercase',
              color: T.inkMuted, marginBottom: 6,
            }}>
              Suggested mitigation
            </div>
            <div style={{ fontFamily: T.sans, fontSize: 13.5, color: T.ink2, lineHeight: 1.5 }}>
              {finding.mitigation}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => onUpdateSpec(finding.id)}
              disabled={updateSpecRunning}
              style={{
                fontFamily: T.sans, fontSize: 14, fontWeight: 700,
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: updateSpecRunning ? T.inkFaint : T.ink,
                color: '#FFFFFF',
                cursor: updateSpecRunning ? 'default' : 'pointer',
              }}
            >
              {updateSpecRunning ? 'Updating…' : 'Update spec'}
            </button>
            <button
              type="button"
              disabled
              title="Coming soon"
              style={{
                fontFamily: T.sans, fontSize: 14, fontWeight: 600,
                padding: '8px 16px', borderRadius: 8,
                background: '#FFFBEC', color: T.inkFaint,
                border: '1px solid var(--color-border)',
                cursor: 'not-allowed',
              }}
            >
              Open PR to fix code →
            </button>
            <span style={{
              fontFamily: T.hand, fontSize: 16, color: T.inkFaint, marginLeft: 4,
            }}>
              coming soon
            </span>
            <div style={{ flex: 1 }} />
            {onDismiss && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(finding.id);
                }}
                disabled={dismissRunning}
                title="Mark this finding as not applicable. It won't reappear on rescans."
                style={{
                  fontFamily: T.sans, fontSize: 13, fontWeight: 600,
                  padding: '6px 12px', borderRadius: 8,
                  background: 'transparent',
                  color: dismissRunning ? T.inkFaint : T.inkMuted,
                  border: '1px solid var(--color-border)',
                  cursor: dismissRunning ? 'default' : 'pointer',
                }}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
